// data.callBackDate
export default class DatePickerController {
  /* @ngInject */
  constructor(moment, $log) {
    this.moment = moment;
    this.$log = $log;

    this.months = [];
    this.years = [];
    this.monthsAreBuilt = false;
  }

  $onChanges(changes) {
    // flag is set in the parent on radio button change
    if (changes.callMeIsActive && changes.callMeIsActive.currentValue) {
      // builds a date value and adds it to the form payload
      this.updateFormCallbackDate();
    }
  }

  $onInit() {
    // build the date for the current day, month, year
    this.populateDateFields();
  }

  static formatDateValue(val) {
    return val.toString().length === 1 ? `0${val}` : val.toString();
  }

  populateDateFields() {
    this.currentDate = this.currentDate || this.moment();
    const currentDay = this.currentDate.format('DD').toString();
    const currentMonth = this.currentDate.format('MM').toString();
    const currentYear = this.currentDate.format('YYYY').toString();

    this.selectedDay = this.selectedDay || currentDay;
    this.selectedMonth = this.selectedMonth || currentMonth;
    this.selectedYear = this.selectedYear || currentYear;
    this.previousYear = this.previousYear || currentYear;

    // sets the date ranges on the datepicker if required
    // If no value is input into the component only the current year will be available
    const minDate = this.processDate(this.minDate);
    const maxDate = this.processDate(this.maxDate);

    // used when building the years array
    let year;

    // days will be recalculated on every year or month change
    this.days = [];

    // preset the day to current day or the month to the current month
    // this is in place to guard against an invalid date being selected
    // when setting the day or month to an earlier day/month than the current day/month
    // while in a later month, then switching back to the current month
    // or while in a later year, then switching back to the current year
    if (this.selectedMonth === currentMonth && this.selectedDay < currentDay) {
      this.selectedDay = currentDay;
    }
    if (this.selectedYear === currentYear && this.selectedMonth < currentMonth) {
      this.selectedMonth = currentMonth;
      this.selectedDay = currentDay;
    }

    // ensures that weekend days (which are disabled) cannot be selected
    this.setSelectedDayToFirstWeekday();
    // figure out whether the months need to be rebuilt or not
    // if we change from the currentYear to a different year then rebuild
    // if we change from a non-currentYear to the currentYear then rebuild
    // if we are changing from one non-currentYear to another then dont rebuild
    if ((this.selectedYear !== currentYear && this.previousYear === currentYear)
      || (this.selectedYear === currentYear && this.previousYear !== currentYear)) {
      this.monthsAreBuilt = false;
      this.months = [];
    }

    // Build days/months dropdown
    for (let dayOrMonth = 1; dayOrMonth <= 31; dayOrMonth += 1) {
      const value = this.constructor.formatDateValue(dayOrMonth);
      const date = this.moment(`${this.selectedYear}-${this.selectedMonth}-${value}`, 'YYYY-MM-DD');
      const monthDate = this.moment(`${this.selectedYear}-${value}-01`, 'YYYY-MM-DD');

      // process dates in the current year
      if (this.selectedYear === currentYear && date.isValid()) {
        // build months in current year
        if (!this.monthsAreBuilt && dayOrMonth <= 12 && dayOrMonth >= currentMonth) {
          // build a date to get the month name from moment - always set the day to the 1st
          this.buildMonth(monthDate, value);
        }
        // display only remaining days in the current month,
        // or if its a later month display all days
        if (dayOrMonth >= currentDay || this.selectedMonth > currentMonth) {
          this.buildAllDaysInCurrentMonth(date, value);
        }
      // process dates in all other years
      } else if (date.isValid()) {
        // build months in any year other than the current year
        if (!this.monthsAreBuilt && dayOrMonth <= 12) {
          this.buildMonth(monthDate, value);
        }
        this.buildAllDaysInCurrentMonth(date, value);
      }
    }
    this.monthsAreBuilt = true;
    this.previousYear = this.selectedYear;

    // Build years dropdown
    if (angular.equals(this.years, [])) {
      if (maxDate.year() <= currentYear) {
        for (year = maxDate.year(); year >= minDate.year(); year -= 1) {
          this.years.push({ name: year, value: `${year}` });
        }
      } else {
        for (year = minDate.year(); year <= maxDate.year(); year += 1) {
          this.years.push({ name: year, value: `${year}` });
        }
      }
    }

    // this needs to happen whenever the date picker is rebuilt
    // so that auto selected values are formed into a data and placed in the form payload
    if (this.callMeIsActive) {
      this.updateFormCallbackDate();
    }
  }

  updateFormCallbackDate() {
    this.form.callbackDate = `${this.selectedDay}/${this.selectedMonth}/${this.selectedYear}`;
  }

  // Weekdays need to be separated from weekends as weekend days are
  // outside busines hours and therefore not selectable
  selectedDayIsWeekday(dayNumber) {
    const dateName = this.formatDayDateAsName(this.selectedYear, this.selectedMonth, dayNumber);
    return dateName !== 'Sat' && dateName !== 'Sun';
  }

  // When switching from one month to another where a particular day is selected
  // if the currently selected day in the new month is a weekend day or an invalid date
  // increase or decrease the selected day number until the first valid weekday is found
  // dateIsAtMonthEnd ensure that the date is decremented till a valid date is returned
  setSelectedDayToFirstWeekday(dateIsAtMonthEnd) {
    const selectedDayAsNumber = this.selectedDay;
    const selectedDayAsMoment = this.moment(`${this.selectedYear}-${this.selectedMonth}-${selectedDayAsNumber}`, 'YYYY-MM-DD');
    let dateIsAtMonthEndFlag = dateIsAtMonthEnd || false;

    let adjustmentValue;
    // an example of an invalid dates is September 31st or February 30th
    // if the date is invalid we are trying to process a day at the end of the month
    // so we then need to decrement the date number till we find a valid date
    if (!selectedDayAsMoment.isValid()) {
      dateIsAtMonthEndFlag = true;
      adjustmentValue = -1;
      this.setDayToNextOrPreviousDay(selectedDayAsNumber, adjustmentValue);
      this.setSelectedDayToFirstWeekday(dateIsAtMonthEndFlag);
      // weekend days are not allowed
    } else if (!this.selectedDayIsWeekday(selectedDayAsNumber)) {
      if (dateIsAtMonthEndFlag) {
        adjustmentValue = -1;
        this.setDayToNextOrPreviousDay(selectedDayAsNumber, adjustmentValue);
        this.setSelectedDayToFirstWeekday(dateIsAtMonthEndFlag);
      } else {
        adjustmentValue = 1;
        this.setDayToNextOrPreviousDay(selectedDayAsNumber, adjustmentValue);
        this.setSelectedDayToFirstWeekday();
      }
    }
  }

  setDayToNextOrPreviousDay(selectedDay, adjustmentValue) {
    let selectedDayNumber = parseInt(selectedDay, 10);
    selectedDayNumber += adjustmentValue;
    selectedDayNumber = this.constructor.formatDateValue(selectedDayNumber);
    this.selectedDay = selectedDayNumber;
  }

  // Use moment to return the day name being built
  formatDayDateAsName(year, month, day) {
    const date = this.moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
    return date.format('ddd');
  }

  buildMonth(date, value) {
    const monthName = date.format('MMMM');
    this.months.push({ name: monthName, value });
  }

  buildAllDaysInCurrentMonth(date, value) {
    const dateNumber = date.date();
    const dateName = this.formatDayDateAsName(this.selectedYear, this.selectedMonth, dateNumber);
    // 'Invalid date' is returned when the day number (eg 31 in September)
    // does not exist in that month
    if (dateName !== 'Invalid date' && isFinite(dateNumber)) {
      // weekend days need to be marked as disabled
      if (!this.selectedDayIsWeekday(dateNumber)) {
        this.days.push({ name: `${value} ${dateName}`, value, disable: true });
        // weekdays do not required a disabled flag
      } else {
        this.days.push({ name: `${value} ${dateName}`, value });
      }
    }
  }

  // defines either the range of years available to the datepicker or
  // a specific date available as the maximum or minimum date
  processDate(dateStr) {
    const currentDate = this.moment();
    let unit;
    let value;

    if (dateStr && dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
      return this.moment(dateStr);
    } else if (dateStr && dateStr.match(/^-?\d+[yQMwdhms]$/)) {
      unit = dateStr.substr(-1);
      value = parseInt(dateStr.substr(0, dateStr.length - 1), 10);
      return currentDate.add(value, unit);
    }
    return currentDate;
  }
}



// WEBPACK FOOTER //
// ./src/components/date-picker/date-picker.controller.js